import { useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Numeric } from '@blade/ui-kit'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import { Trend } from '@root/entities/trend'
import { UserTradingAnalyticsPriceType } from '@root/features/user/user-trading-analytics/models/user-trading-analytics-price-type'
import {
  CURRENCY_AMOUNT_NATIVE_PRESET,
  PnlUsdFormatted,
} from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'
import { mediaMaxMobile, ReservedSpace, Skeleton } from '@root/shared/ui-kit'

import type { UserTradingAnalytics } from '@root/features/user/user-trading-analytics/models/user-trading-analytics'
import type { Error } from '@root/shared'
import type { TransKey } from '@root/shared/localization'
import type BigNumber from 'bignumber.js'
import type { ReactNode } from 'react'

type UserPnlSummaryProps = {
  period: TransKey
  priceType: UserTradingAnalyticsPriceType
  requestState: AsyncStateN.AsyncStateN<
    Error.UnknownError,
    UserTradingAnalytics
  >
}

type SummaryColParts = {
  title: ReactNode
  main: ReactNode
  percent?: ReactNode
  trend?: Trend.Trend
}

export const UserPnlSummary: React.FC<UserPnlSummaryProps> = ({
  period,
  priceType,
  requestState,
}) => {
  const { t } = useTranslation()

  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)

  const formatValue = (
    usdValue: BigNumber,
    nativeValue: BigNumber,
    priceType: UserTradingAnalyticsPriceType,
  ) => {
    return priceType === UserTradingAnalyticsPriceType.USD ? (
      <PnlUsdFormatted pnl={usdValue} />
    ) : (
      <Numeric
        forceSign
        value={nativeValue}
        preset={CURRENCY_AMOUNT_NATIVE_PRESET}
        currency={nativeCurrency.ticker}
      />
    )
  }

  const periodTitle = (
    <>
      <ReservedSpace $chars={3}>{t(period)}</ReservedSpace>{' '}
      {t('portfolio.pnl-chart.title')}
    </>
  )

  const skeletonMain = <Skeleton width={40} height={20} />

  return AsyncStateN.matchWaiting(requestState, {
    Error: () => (
      <Root>
        {renderSummaryCol({ title: periodTitle, main: '-' })}
        {renderSummaryCol({
          title: t('common.win-rate'),
          main: '-',
        })}
        {renderSummaryCol({ title: t('common.pnl-total'), main: '-' })}
        {renderSummaryCol({
          title: t('common.unrealized-profit'),
          main: '-',
        })}
      </Root>
    ),
    Waiting: () => (
      <Root>
        {renderSummaryCol({
          title: periodTitle,
          main: skeletonMain,
          percent: '—',
        })}
        {renderSummaryCol({
          title: t('common.win-rate'),
          main: skeletonMain,
        })}
        {renderSummaryCol({
          title: t('common.pnl-total'),
          main: skeletonMain,
          percent: '—',
        })}
        {renderSummaryCol({
          title: t('common.unrealized-profit'),
          main: skeletonMain,
        })}
      </Root>
    ),
    Success: (data: UserTradingAnalytics) => {
      const {
        winRate,
        totalPnlUsdChangePercent,
        realizedPnlUsdChangePercent,
        // usd
        totalPnlUsd,
        unrealizedPnlUsd,
        realizedPnlUsd,
        // native
        totalPnlNative,
        unrealizedPnlNative,
        realizedPnlNative,

        // winRateChangePercent,
        // unrealizedPnlUsdChangePercent,
      } = data.performance

      return (
        <Root>
          {renderSummaryCol({
            title: periodTitle,
            main: formatValue(realizedPnlUsd, realizedPnlNative, priceType),
            percent: percentChip(realizedPnlUsdChangePercent),
            trend: Trend.of(realizedPnlUsd),
          })}
          {renderSummaryCol({
            title: t('common.win-rate'),
            main: (
              <>
                <Numeric forceSign value={winRate} preset="percents" />%
              </>
            ),
          })}
          {renderSummaryCol({
            title: t('common.pnl-total'),
            main: formatValue(totalPnlUsd, totalPnlNative, priceType),
            percent: percentChip(totalPnlUsdChangePercent),
            trend: Trend.of(totalPnlUsd),
          })}
          {renderSummaryCol({
            title: t('common.unrealized-profit'),
            main: formatValue(unrealizedPnlUsd, unrealizedPnlNative, priceType),
            trend: Trend.of(unrealizedPnlUsd),
          })}
        </Root>
      )
    },
  })
}

const renderSummaryCol = ({ title, main, percent, trend }: SummaryColParts) => (
  <Col data-trend={trend}>
    <Title>{title}</Title>
    <Value>{main}</Value>
    {percent === undefined ? undefined : <Percentage>{percent}</Percentage>}
  </Col>
)

const percentChip = (n: BigNumber | number | undefined) =>
  n === undefined ? (
    '—'
  ) : (
    <>
      <Numeric value={n} preset="percents" forceSign />%
    </>
  )

const Root = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: 10px;
  padding-inline: 16px;
  padding-top: 20px;
  align-items: start;

  ${mediaMaxMobile(css`
    grid-template-columns: repeat(2, auto);
    gap: 14px;
    padding-inline: 14px;
  `)}
`

const Col = styled.div`
  display: grid;
  place-items: center;
  align-items: center;
  justify-content: start;
  justify-items: start;
  gap: 10px;
  color: var(--text-12);
  align-content: center;

  grid-template-areas:
    'title'
    'value'
    'percent';

  &[data-trend='up'] {
    color: var(--up-10);
  }
  &[data-trend='down'] {
    color: var(--down-9);
  }
  &[data-trend='neutral'] {
    color: var(--textA-9);
  }

  ${mediaMaxMobile(css`
    gap: 4px;

    grid-template-areas:
      'title title'
      'value percent';
    grid-template-columns: min-content;
  `)}
`

const Title = styled.div`
  color: var(--textA-9);
  grid-area: title;

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`

const Value = styled.div`
  font-size: 26px;
  font-weight: 700;
  line-height: 20px;
  grid-area: value;

  ${mediaMaxMobile(css`
    font-size: 18px;
  `)}
`

const Percentage = styled.div`
  background: var(--backgroundA-3);

  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  border-radius: 55px;
  padding: 2px 8px;
  grid-area: percent;
`
